import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
  STREVDA_SLOTS,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isResponseContainFlightsBody } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';
import { isRadarFlightsResponseError } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/radarFlightsResponse';
import keyBy from 'lodash/keyBy';

import { transformRadarCreativeToAdvert } from './transformRadarCreativeToAdvert';
import transformStrevdaSlotToAdvert from './transformStrevdaSlotToAdvert';

import type {
  RadarResponsePayload,
  RadarFlightBodyPayload,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarResponsePayloadTypes';
import type { Slot } from '@web-platform/shared-universal-types/src/network/endpoints/strevda';
import type { StrevdaSlotsPayloadResponseSuccess } from '@web-platform/shared-universal-types/src/network/types';
import type {
  MutationResponseAction,
  QueryResponseAction,
  Action,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  CombinedResultsFlightsInlineAdvert,
  CombinedResultsFlightsInlineAdvertById,
  CombinedResultsFlightsInlineState,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/combinedResultsFlightsInline';

export type { CombinedResultsFlightsInlineState, Slot };
export const INITIAL_STATE: CombinedResultsFlightsInlineState = {
  byId: {},
};

const generateNewState = (
  state: CombinedResultsFlightsInlineAdvertById,
  adverts: CombinedResultsFlightsInlineAdvert[],
): CombinedResultsFlightsInlineAdvertById => {
  const newAdvertsById: CombinedResultsFlightsInlineAdvertById = keyBy(
    adverts,
    (advert) => advert.id,
  );

  const combinedAdvertsById = { ...state, ...newAdvertsById };

  return combinedAdvertsById;
};

const mapStrevdaSlotsToCombinedResultsFlightsInlineState = (
  response: StrevdaSlotsPayloadResponseSuccess,
): CombinedResultsFlightsInlineAdvert[] => {
  const { body: slots } = response;

  return slots.map(transformStrevdaSlotToAdvert);
};

type CombinedResultsFlightsInlineAdvertWithPosition = CombinedResultsFlightsInlineAdvert & {
  position?: number;
};

const mapRadarCreativesToCombinedResultsFlightsInlineState = (
  flightsResponseBody: RadarFlightBodyPayload,
): CombinedResultsFlightsInlineAdvertWithPosition[] => {
  const { creatives } = flightsResponseBody;

  if (creatives && creatives.length > 0) {
    const adverts: CombinedResultsFlightsInlineAdvertWithPosition[] = [];
    creatives.forEach((creative) => {
      const advert = transformRadarCreativeToAdvert(creative);
      if (advert) {
        adverts.push(advert);
      }
    });

    return adverts;
  }
  return [];
};

export const extractCombineResultsInlineAdverts = (
  response: RadarResponsePayload,
): CombinedResultsFlightsInlineAdvertWithPosition[] | null => {
  if (isRadarFlightsResponseError(response) || !isResponseContainFlightsBody(response)) {
    return null;
  }
  return mapRadarCreativesToCombinedResultsFlightsInlineState(response.body.itineraries);
};

const byId = (
  state: CombinedResultsFlightsInlineAdvertById,
  action: MutationResponseAction | QueryResponseAction,
) => {
  switch (action.payload.type) {
    case RADAR_CREATE_SESSIONS:
    case RADAR_POLL: {
      const adverts = extractCombineResultsInlineAdverts(action.payload.response);

      if (!adverts) {
        return state;
      }

      return generateNewState(state, adverts);
    }

    case STREVDA_SLOTS: {
      if (action.payload.response.error) {
        return state;
      }

      const adverts = mapStrevdaSlotsToCombinedResultsFlightsInlineState(action.payload.response);
      return generateNewState(state, adverts);
    }

    default:
      return state;
  }
};

const combinedResultsFlightsInlineReducer = (
  state: CombinedResultsFlightsInlineState = INITIAL_STATE,
  action: Action,
): CombinedResultsFlightsInlineState => {
  switch (action.type) {
    case MUTATION_RESPONSE_RECEIVED:
    case QUERY_RESPONSE_RECEIVED: {
      const newById = byId(state.byId, action);
      if (newById === state.byId) return state;
      return { byId: newById };
    }

    case SEARCH_PARAMS_CHANGED: {
      return INITIAL_STATE;
    }

    default:
      return state;
  }
};

export default combinedResultsFlightsInlineReducer;

export { getAll, get } from './selectors';
